import { create } from 'zustand'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuthStore, type RoleEntry } from './auth'
import { USER_ROLES, type UserRole } from '../constants/roles'

interface ImpersonationState {
  targetUid: string | null
  targetName: string | null
  targetEmail: string | null
  targetRoles: RoleEntry[] | null
  startImpersonation: (uid: string, name: string, email: string, roles: RoleEntry[]) => Promise<void>
  stopImpersonation: () => Promise<void>
}

async function writeAuditLog(action: 'view_as_start' | 'view_as_end', targetUid: string, targetName: string) {
  const auth = useAuthStore.getState()
  try {
    await addDoc(collection(db, 'admin_audit_log'), {
      adminUid: auth.uid(),
      adminEmail: auth.user?.email ?? null,
      targetUid,
      targetName,
      action,
      timestamp: serverTimestamp(),
    })
  } catch (err) {
    console.warn('[impersonation] audit log write failed:', err)
  }
}

export const useImpersonationStore = create<ImpersonationState>()((set, get) => ({
  targetUid: null,
  targetName: null,
  targetEmail: null,
  targetRoles: null,

  async startImpersonation(uid, name, email, roles) {
    await writeAuditLog('view_as_start', uid, name)
    set({ targetUid: uid, targetName: name, targetEmail: email, targetRoles: roles })
  },

  async stopImpersonation() {
    const { targetUid, targetName } = get()
    if (targetUid && targetName) {
      await writeAuditLog('view_as_end', targetUid, targetName)
    }
    set({ targetUid: null, targetName: null, targetEmail: null, targetRoles: null })
  },
}))

/** Hook: returns the impersonated user's roles when active, otherwise the real user's roles. */
export function useEffectiveRoles(): RoleEntry[] {
  const targetRoles = useImpersonationStore((s) => s.targetRoles)
  const authRoles = useAuthStore((s) => s.roles())
  return targetRoles ?? authRoles
}

/** Non-hook equivalent for use inside store functions. */
export function getEffectiveRoles(): RoleEntry[] {
  return useImpersonationStore.getState().targetRoles ?? useAuthStore.getState().roles()
}

/** Hook: derives primary role from the effective roles. */
export function useEffectivePrimaryRole(): UserRole {
  const roles = useEffectiveRoles()
  if (roles.length > 0) {
    const orgRole = roles.find(
      (r) => r.role === USER_ROLES.SUPER_ADMIN || r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF,
    )
    if (orgRole) return orgRole.role
    return roles[0].role
  }
  return USER_ROLES.FAMILY_ADMIN
}
